import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/EnterpriseAuthContext';

/** Edge function that serves the platform console (read-only, allowlisted server side). */
export const PLATFORM_ADMIN_FUNCTION = 'platform-admin';

/** Route of the console page. */
export const CONSOLE_PATH = '/console';

/** sessionStorage key prefix for a negative probe (keeps non-admins at one probe per tab session). */
const PROBE_STORAGE_PREFIX = 'quillai-console-probe:';

/** Error from the platform-admin function. `status` 404 means "not a platform admin". */
export class PlatformAdminError extends Error {
  /**
   * @param {string} message - Message.
   * @param {number} status - HTTP status (0 when the request never got a reply).
   * @param {boolean} [fromServer=false] - The status came from a server reply (not a local check).
   */
  constructor(message, status, fromServer = false) {
    super(message);
    this.name = 'PlatformAdminError';
    this.status = status;
    this.fromServer = fromServer;
  }
}

/**
 * Whether an error means the caller may not use the console (the function answers 404).
 * @param {unknown} error - Error.
 * @returns {boolean} True for 401/403/404.
 */
export function isConsoleDenied(error) {
  return error instanceof PlatformAdminError && [401, 403, 404].includes(error.status);
}

/**
 * Current access token, or null.
 * @returns {Promise<string|null>} Token.
 */
async function accessToken() {
  try {
    const result = await supabase?.auth?.getSession?.();
    return result?.data?.session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Call one platform-admin action.
 * @param {'whoami'|'overview'|'tenants'|'tenant_users'|'isolation_checks'} action - Action.
 * @param {object} [params] - Extra body fields (e.g. { accountId }).
 * @param {{signal?: AbortSignal}} [options] - Fetch options.
 * @returns {Promise<object>} Parsed JSON reply.
 * @throws {PlatformAdminError} On any non-2xx reply or network failure.
 */
export async function callPlatformAdmin(action, params = {}, options = {}) {
  if (typeof fetch !== 'function') throw new PlatformAdminError('Network unavailable.', 0);
  const token = await accessToken();
  if (!token) throw new PlatformAdminError('Not signed in.', 401);
  const base = process.env.REACT_APP_SUPABASE_URL || '';
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  let response;
  try {
    response = await fetch(`${base}/functions/v1/${PLATFORM_ADMIN_FUNCTION}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(anonKey ? { apikey: anonKey } : {}),
      },
      body: JSON.stringify({ ...params, action }),
      signal: options.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new PlatformAdminError('Could not reach the console service. Check your connection and try again.', 0);
  }
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const message = (body && (body.error || body.message)) || `Request failed (${response.status}).`;
    throw new PlatformAdminError(String(message), response.status, true);
  }
  return body || {};
}

/** userId -> true | false | Promise<boolean>. */
const probeCache = new Map();

/** How long a probe that failed on the network (no reply) stays cached. */
const NETWORK_NEGATIVE_MS = 10 * 60 * 1000;

/**
 * Read a cached negative probe for this tab session.
 * @param {string} userId - User id.
 * @returns {boolean} True when this user already probed negative (and it has not expired).
 */
function readNegative(userId) {
  try {
    const raw = window.sessionStorage.getItem(PROBE_STORAGE_PREFIX + userId);
    if (raw === '0') return true;
    const until = Number(raw);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

/**
 * Remember a negative probe: for the tab session when the server said no (401/403/404), for a
 * few minutes when the request never got a reply (so a flaky network does not hide the entry
 * for the owner all session, and an undeployed function is not probed on every page load).
 * @param {string} userId - User id.
 * @param {boolean} definitive - The server answered.
 */
function writeNegative(userId, definitive) {
  try {
    window.sessionStorage.setItem(PROBE_STORAGE_PREFIX + userId, definitive ? '0' : String(Date.now() + NETWORK_NEGATIVE_MS));
  } catch {
    // Storage blocked: the in-memory cache still applies.
  }
}

/**
 * Ask the server whether this user may open the console ({action: 'whoami'}). One request per
 * user per page load (negatives also cached for the tab session). The server re-checks every
 * console call, so this only decides whether the nav entry shows.
 * @param {string} userId - Signed-in user id.
 * @returns {Promise<boolean>} Allowed.
 */
export function probePlatformAdmin(userId) {
  if (!userId) return Promise.resolve(false);
  const cached = probeCache.get(userId);
  if (cached !== undefined) return Promise.resolve(cached);
  if (readNegative(userId)) {
    probeCache.set(userId, false);
    return Promise.resolve(false);
  }
  const pending = callPlatformAdmin('whoami').then(
    (reply) => ({ allowed: reply?.ok === true, definitive: true }),
    (error) => ({ allowed: false, definitive: isConsoleDenied(error) && error.fromServer === true })
  ).then(({ allowed, definitive }) => {
    probeCache.set(userId, allowed);
    if (!allowed) writeNegative(userId, definitive);
    return allowed;
  });
  probeCache.set(userId, pending);
  return pending;
}

/**
 * Synchronous cached probe result, or null when unknown.
 * @param {string|null|undefined} userId - User id.
 * @returns {boolean|null} Cached result.
 */
function cachedProbe(userId) {
  const value = userId ? probeCache.get(userId) : undefined;
  return typeof value === 'boolean' ? value : null;
}

/** Clear the probe cache (tests, sign-out). */
export function resetPlatformAdminProbe() {
  probeCache.clear();
}

/**
 * Whether the signed-in user may see the console nav entries. False until the probe says ok.
 * @returns {boolean} Allowed.
 */
export function usePlatformAdminAccess() {
  const { state } = useAuth();
  const userId = state?.isAuthenticated ? state?.user?.id || null : null;
  const [allowed, setAllowed] = useState(() => cachedProbe(userId) === true);

  useEffect(() => {
    if (!userId) {
      setAllowed(false);
      return undefined;
    }
    const known = cachedProbe(userId);
    if (known !== null) {
      setAllowed(known);
      return undefined;
    }
    let live = true;
    probePlatformAdmin(userId).then(
      (value) => {
        if (live) setAllowed(value);
      },
      () => {
        if (live) setAllowed(false);
      }
    );
    return () => {
      live = false;
    };
  }, [userId]);

  return allowed;
}
